import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild
} from '@angular/core'
import { SaleItem } from '../sale-item'
import { AppState } from 'src/app/core/core.state'
import { Store } from '@ngrx/store'
import { saleItemActions } from '../sale-item.actions'
import { bookableSlotActions } from '../../../bookable-slot/bookable-slot.actions'
import { saleItemSelectors } from '../sale-item.selectors'
import { catchError, take, timeout, withLatestFrom } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import { NotificationService } from 'src/app/core/notification.service'
import { combineLatest, Observable, of } from 'rxjs'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { paymentActions } from '../../payment/payment.actions'
import { saleActions } from '../../sale.actions'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { environment } from '../../../../../environments/environment'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { getNumber } from 'src/app/shared/until'
import { saleSelectors } from '../../sale.selectors'
import { Sale } from '../../sale'
import { Router } from '@angular/router'
import { TypeOfProduct } from 'src/app/shared/constants'
import { SaleItemFormCreateSubmit } from '../form-create/form.component'
import { saleItemSurveyDraftActions } from '../../sale-item-survey-draft/sale-item-survey-draft.actions'
import { SaleItemSurveyDraft } from '../../sale-item-survey-draft/sale-item-survey-draft.model'
import { SaleItemFormCreateComponent } from '../form-create/form.component'
import { SubsSaleItemFormCreateComponent } from '../subs-form-create/form.component'
import { RentalSaleItemFormCreateComponent } from '../rental-form-create/form.component'
import { SaleItemCreateFormState } from '../create-form-state.model'
import { answerActions } from 'src/app/features/survey/answer/answer.actions'

type SaleItemCreate = 'single' | 'multiple' | 'subscription' | 'course' | 'rental'
@UntilDestroy()
@Component({
  selector: 'sale-item-create-modal',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleItemCreateModalComponent implements OnInit {
  @Input() title: string
  @Input() saleItem: Partial<SaleItem>
  @Input() saleId: number
  @Input() selectedCustomerPersonId: number
  @Input() customerCategoryId: number | string | null
  @Input() saleItemCreate: SaleItemCreate = null
  @Input() isPage: boolean = false
  @Output() pageClose = new EventEmitter<void>()
  @ViewChild(SaleItemFormCreateComponent)
  saleItemFormCreateComponent: SaleItemFormCreateComponent
  @ViewChild(SubsSaleItemFormCreateComponent)
  subsSaleItemFormCreateComponent: SubsSaleItemFormCreateComponent
  @ViewChild(RentalSaleItemFormCreateComponent)
  rentalSaleItemFormCreateComponent: RentalSaleItemFormCreateComponent

  public totalSalesToCreate: number = 0
  public totalSalesCreate: number = 0
  public totalSalesToPay: number = 0
  public totalSalesPay: number = 0
  public loading: boolean = false
  public isMultiDayEnabled = environment.moduleConfigs.saleModule.multiDayFlow
  public isLoading$: Observable<boolean>
  public currentFormState: SaleItemCreateFormState | null = null

  constructor(
    private router: Router,
    @Optional() private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(saleItemSelectors.selectIsLoading)

    if (!this.selectedCustomerPersonId && this.saleId) {
      this.store
        .select(saleSelectors.selectById, this.saleId)
        .pipe(take(1))
        .subscribe((sale) => {
          if (sale?.customerPersonId) {
            this.selectedCustomerPersonId = sale.customerPersonId
          }
        })
    }

    this.store
      .select(saleItemSelectors.selectByFilter, {
        field: 'saleId',
        value: this.saleId
      })
      .pipe(
        take(1),
        withLatestFrom(this.store.select(saleSelectors.selectPartial))
      )
      .subscribe(([saleItem, salePartial]) => {
        if (saleItem.length > 0) {
          this.customerCategoryId = saleItem[0].customerCategory
        }
      })
  }

  public cancel() {
    this.saleItem = null
    this.bsModalRef?.hide()
    this.pageClose.emit()
    this.store.dispatch(bookableSlotActions.setSelected({ entity: null }))
  }

  public get activeMode(): SaleItemCreate | null {
    if (this.saleItemCreate) {
      return this.saleItemCreate
    }

    if (!this.saleId) {
      return 'single'
    }

    return null
  }

  public get shouldShowFooterSaveButton(): boolean {
    return (
      this.activeMode === 'single' ||
      this.activeMode === 'subscription' ||
      this.activeMode === 'rental'
    )
  }

  public get isSaveButtonDisabled(): boolean {
    return !this.currentFormState?.canSubmit
  }

  public get saveButtonLabelKey(): string {
    if (this.currentFormState?.submitLabelKey) {
      return this.currentFormState.submitLabelKey
    }

    return 'general.save'
  }

  public onFormStateChange(state: SaleItemCreateFormState) {
    this.currentFormState = state
  }

  public onClickSave() {
    if (this.activeMode === 'subscription') {
      this.subsSaleItemFormCreateComponent?.submitFromParent()
      return
    }

    if (this.activeMode === 'single') {
      this.saleItemFormCreateComponent?.submitFromParent()
      return
    }

    if (this.activeMode === 'rental') {
      this.rentalSaleItemFormCreateComponent?.submitFromParent()
    }
  }

  public onSubmitRental(saleItem: Partial<SaleItem>) {
    if (this.saleId) {
      this.updateSaleWithSaleItem(this.saleId, saleItem)
      this.notificationService.emit('alerts.success', 'success')
      this.cancel()
      return
    }

    const entity = {
      ...this.saleItem,
      ...saleItem,
      id: Date.now() + Math.floor(Math.random() * 1000)
    }

    this.store.dispatch(
      saleItemActions.createOnePartial({
        entity
      })
    )

    this.notificationService.emit('alerts.success', 'success')
    this.cancel()
  }

  public onSubmitSubscription(sale: Partial<Sale>) {
    this.store.dispatch(
      saleActions.createOne({
        entity: {
          ...sale
        }
      })
    )
    this.actions$
      .pipe(
        ofType(
          saleActions.upsertMany,
          saleActions.createOneFailed,
          saleActions.updateOneById,
          saleActions.upsertOne
        ),
        take(1),
        timeout(10000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          console.error('Failed to create sale timeout')
          this.notificationService.emit('alerts.warning', 'warning')
          return false
        }
        if (action.type == saleActions.createOneFailed({}).type) {
          console.error('Failed to create sale')
          return false
        }
        const actionWithEntities = action as { entities?: any[] }
        if (
          Array.isArray(actionWithEntities.entities) &&
          actionWithEntities.entities.length > 0
        ) {
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
          this.router.navigate([`/sales/${actionWithEntities.entities[0].id}`])
          return
        } else {
          console.error('Failed to create sale no entities')
          this.notificationService.emit('alerts.warning', 'warning')
          return false
        }
      })
  }
  public onSubmit(result: SaleItemFormCreateSubmit) {
    const saleItem = result.saleItem
    const surveyDraft = result.surveyDraft

    if (this.saleId) {
      this.updateSaleWithSaleItem(this.saleId, saleItem, surveyDraft)
      this.notificationService.emit('alerts.success', 'success')
      this.cancel()
      return
    }

    let saleItemPartialsToBeCreated = []

    for (let category = 0; category < 10; category++) {
      const saleItemToInsert = {
        ...saleItem,
        customerCategory: Number(category),
        params: this.getCategoryScopedParams(saleItem, category)
      }

      if (saleItem?.params?.[`invitedCategory${category}`]?.length) {
        saleItemPartialsToBeCreated.push(saleItemToInsert)
      }
    }

    saleItemPartialsToBeCreated.forEach((saleItemPartial) => {
      let entity = { ...this.saleItem, ...saleItemPartial }
      // unique tmp ID to distinguish on same entities being different sale items
      entity.id = Date.now() + Math.floor(Math.random() * 1000)

      this.store.dispatch(
        saleItemActions.createOnePartial({
          entity
        })
      )

      this.persistSurveyDraftForSaleItem(entity.id, entity, surveyDraft)
    })

    // because of the category feature this partial has to be created if there is at least 1 invite or the booker has to be participant
    this.store
      .select(productSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((productEntities) => {
        const participantAmount =
          getNumber(saleItem?.params?.invitedGuests?.length) +
          getNumber(saleItem?.params?.invitedOthers?.length) +
          getNumber(saleItem?.params?.invitedMemberEmails?.length) +
          (productEntities[saleItem?.params?.bookableLinkedProductId]
            ?.bookerMustBeParticipant
            ? 1
            : 0)

        if (
          (participantAmount > 0 &&
            saleItem.typeProduct === TypeOfProduct.BOOKING) ||
          saleItem.typeProduct === TypeOfProduct.OTHERPRODUCT
        ) {
          let entity = { ...this.saleItem, ...saleItem }
          // unique tmp ID to distinguish on same entities being different sale items
          entity.id = Date.now() + Math.floor(Math.random() * 1000)

          this.store.dispatch(
            saleItemActions.createOnePartial({
              entity
            })
          )

          this.persistSurveyDraftForSaleItem(entity.id, entity, surveyDraft)
        }
      })

    this.notificationService.emit('alerts.success', 'success')

    this.cancel()
  }

  private getCategoryScopedParams(
    saleItem: Partial<SaleItem>,
    category: number
  ): Partial<SaleItem['params']> {
    const params = { ...(saleItem.params ?? {}) }

    for (let currentCategory = 0; currentCategory < 10; currentCategory++) {
      if (currentCategory !== category) {
        delete params[`invitedCategory${currentCategory}`]
      }
    }

    return params
  }

  private updateSaleWithSaleItem(
    saleId: number,
    saleItem: Partial<SaleItem>,
    surveyDraft?: Partial<SaleItemSurveyDraft> | null
  ) {
    console.log(`Updating sale #${saleId}`)

    combineLatest([
      this.store.select(saleItemSelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      }),
      this.store.select(productSelectors.selectById, saleItem.productId)
    ])
      .pipe(take(1))
      .subscribe(([saleItems, product]) => {
        const maxSaleLine = saleItems.reduce(
          (max, saleItem) =>
            saleItem.saleLine > max ? saleItem.saleLine : max,
          saleItems[0].saleLine
        )

        this.store.dispatch(
          saleItemActions.createOneBySaleId({
            saleId: saleId,
            params: {
              ...this.saleItem,
              ...saleItem,
              saleLine: maxSaleLine + 1
            }
          })
        )

        this.actions$
          .pipe(
            ofType(saleItemActions.upsertMany),
            take(1),
            timeout(60000),
            catchError(() => {
              console.error('Failed to create saleItem before timeout')
              this.notificationService.emit('alerts.error', 'error')
              this.loading = false
              this.cancel()
              return of(null)
            })
          )
          .subscribe((action) => {
            if (!action) {
              console.error('Failed to create saleItem before timeout')
              return false
            }

            const answersToCreate = this.buildAnswersToCreate(
              surveyDraft,
              saleId
            )

            if (answersToCreate.length) {
              this.store.dispatch(
                answerActions.createMany({
                  entities: answersToCreate
                })
              )
            }

            this.store.dispatch(
              saleActions.fetchOneById({
                id: saleId,
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'creditSales' },
                    { field: 'organization' },
                    { field: 'saleItems' },
                    { field: 'saleItems.booking' },
                    { field: 'saleItems.booking.product' },
                    { field: 'saleItems.booking.linkedProduct' },
                    { field: 'saleItems.product' },
                    { field: 'saleItems.product.translations' },
                    { field: 'saleItems.participations' },
                    { field: 'payments' }
                  ]
                })
              })
            )
          })
      })
  }

  private buildAnswersToCreate(
    surveyDraft: Partial<SaleItemSurveyDraft> | null | undefined,
    saleId: number
  ) {
    if (!surveyDraft?.isValid || !surveyDraft?.answers?.length) {
      return []
    }

    return surveyDraft.answers.map((answer) => ({
      ...answer,
      saleId: Number(saleId),
      bookingId: Number(surveyDraft.bookingId),
      memberId: Number(this.selectedCustomerPersonId)
    }))
  }

  private persistSurveyDraftForSaleItem(
    saleItemId: number,
    saleItem: Partial<SaleItem>,
    surveyDraft?: Partial<SaleItemSurveyDraft> | null
  ) {
    if (!surveyDraft?.hasSurvey || !surveyDraft?.surveyId) {
      return
    }

    this.store.dispatch(
      saleItemSurveyDraftActions.upsertOne({
        draft: {
          saleItemId,
          surveyId: surveyDraft.surveyId,
          bookingId:
            saleItem.bookingId ??
            saleItem.params?.bookingId ??
            surveyDraft.bookingId,
          bookableProductId:
            saleItem.params?.bookableProductId ?? surveyDraft.bookableProductId,
          bookableLinkedProductId:
            saleItem.params?.bookableLinkedProductId ??
            saleItem.productId ??
            surveyDraft.bookableLinkedProductId,
          hasSurvey: true,
          isValid: Boolean(surveyDraft.isValid),
          answers: surveyDraft.answers ?? []
        }
      })
    )
  }

  onSubmitMulti(saleItems: Partial<SaleItem>[]) {
    if (!this.saleId) {
      if (!saleItems || saleItems?.length === 0) {
        console.log(
          'No processing is carried out as the number of saleItem is 0'
        )
        this.loading = false
        this.cancel()
        return
      }

      let partialSalesItems = []
      let existsSaleItem = false

      this.store
        .select(saleItemSelectors.selectPartials)
        .pipe(take(1))
        .subscribe((partials) => {
          partialSalesItems = partials
        })

      saleItems.forEach((saleItem) => {
        partialSalesItems.forEach((partialSaleItem) => {
          const partial = JSON.parse(JSON.stringify(partialSaleItem))
          delete partial['id']

          if (JSON.stringify(partial) === JSON.stringify(saleItem)) {
            existsSaleItem = true
          }
        })
      })

      if (!existsSaleItem) {
        this.store.dispatch(
          saleItemActions.createManyPartial({
            entities: saleItems.map((saleItem) => ({
              ...saleItem,
              id: Date.now() + Math.floor(Math.random() * 1000)
            }))
          })
        )
      }

      this.cancel()
      return
    }

    if (this.saleId) {
      this.actions$
        .pipe(
          ofType(paymentActions.updateOneById, saleActions.upsertMany),
          untilDestroyed(this),
          timeout(60000),
          catchError(() => {
            this.loading = false
            this.cancel()
            return of(null)
          })
        )
        .subscribe((action) => {
          if (!action) {
            console.error('Failed to create saleItem before timeout')
            this.cancel()
            return false
          }
          if (action.type.toLowerCase().includes('payments')) {
            this.totalSalesPay++
          }
          if (action.type.toLowerCase().includes('sales')) {
            this.totalSalesCreate++
          }
          if (
            this.totalSalesToCreate === this.totalSalesCreate &&
            this.totalSalesPay === this.totalSalesToPay
          ) {
            this.notificationService.emit('alerts.success', 'success')
            this.loading = false
            this.cancel()
            return
          }
          const actionWithMeta = action as {
            pageCount?: number
            total?: number
            entities?: any[]
          }
          const hasEntities =
            Array.isArray(actionWithMeta.entities) &&
            actionWithMeta.entities.length > 0
          if (
            (actionWithMeta.pageCount ?? 0) === 0 &&
            (actionWithMeta.total ?? 0) === 0 &&
            hasEntities
          ) {
            this.notificationService.emit('alerts.success', 'success')
            this.loading = false
            this.cancel()
            return
          } else {
            this.loading = false
            this.cancel()
            return
          }
        })
      if (!saleItems || saleItems?.length === 0) {
        console.log(
          'No processing is carried out as the number of saleItem is 0'
        )
        this.loading = false
        this.cancel()
        return
      }
      for (const saleItem of saleItems) {
        setTimeout(() => {
          this.updateSaleWithSaleItem(this.saleId, saleItem)
        })
      }
      this.loading = true
    }
  }

  onClickAddSaleItem() {
    this.saleItemCreate = 'single'
    this.currentFormState = null
    this.title = 'general.item'
  }

  onClickAddMultiSaleItem() {
    this.saleItemCreate = 'multiple'
    this.currentFormState = null
  }
  public onClickAddBooking() {
    this.saleItemCreate = 'single'
    this.currentFormState = null
    this.title = 'bookingModule.booking'
  }
}
